import { NoPermissionState } from "@/components/common/no-permission-state";
import { isForbiddenError } from "@/lib/clinic-grants";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Filter } from "lucide-react";

import {
  PageHeader,
  Input,
  Button,
  Skeleton,
} from "@/components/ui";

import {
  CollaboratorDataTable,
  ContainerHeader,
  FormCollaborator,
  FormFilterCollaborator,
} from "@/components/common";

import { useFetchEmployeesQuery, usePermission } from "@/hooks";

export function Collaborators() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { checkPermission } = usePermission();

  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get("page");
  const perPageParam = searchParams.get("per_page");
  const roleId = searchParams.get("roleId");
  const status = searchParams.get("status");

  const [perPage, setPerPage] = useState<number>(
    perPageParam ? Number(perPageParam) : 10
  );
  const [search, setSearch] = useState<string>("");

  const {
    error: listError,
    data: employeesData,
    isLoading,
    refetch: refetchEmployeesData,
  } = useFetchEmployeesQuery({
    page,
    per_page: perPage,
    status,
    search,
    positionsId: roleId,
  });

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set("per_page", newPerPage.toString());
    setSearchParams(newSearchParams);
  };

  useEffect(() => {
    if (!checkPermission("employee", "view employee")) {
      navigate("/")
      return;
    }
    refetchEmployeesData();
  }, [refetchEmployeesData, page, perPage, status, status, roleId, search]);

  // SEC-GATES-02: a refusal from the list endpoint is stated, not shown as an empty list.
  if (isForbiddenError(listError)) return <NoPermissionState />;

  return (
    <section className="space-y-7">
      <div className="space-y-5">
        <PageHeader
          eyebrow="Clínica"
          title={t("LABEL.collaborators")}
          lede="Quem atende na clínica, com cargo, contato e situação de cada profissional."
          actions={
            <FormCollaborator>
              <Button variant="petrol" size="lg" className="px-5">
                {t("LABEL.new")}
              </Button>
            </FormCollaborator>
          }
        />
        <ContainerHeader>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
            aria-label="Pesquisar profissionais"
            placeholder="Pesquisar"
            showError={false}
          />
          <FormFilterCollaborator>
            <Button variant="secondary" className="gap-2">
              <Filter size={14} aria-hidden="true" />
              <span>Filtrar</span>
            </Button>
          </FormFilterCollaborator>
        </ContainerHeader>
      </div>
      <div className="space-y-2">
        {!employeesData || isLoading ? (
          <Skeleton className="w-full h-[500px]" />
        ) : (
          <CollaboratorDataTable
            perPage={perPage}
            handlePerPageChange={handlePerPageChange}
            data={employeesData}
            refetch={() => refetchEmployeesData()}
          />
        )}
      </div>
    </section>
  );
}
